import {
  SyntheticEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { When } from "react-if";

import { CustomIframe } from "../IFrame";
import { Notification } from "../../../common/Atoms/Notification";
import { Paragraph } from "../../../common/Atoms/Typography/Paragraph";

interface StatementPreviewPanelProps {
  html?: string | null;
  maxHeight?: string;
  showDownloadNotice?: boolean;
  downloading?: boolean;
  onDownload?: () => void;
}

export function StatementPreviewPanel({
  html,
  maxHeight = `calc(100vh - 260px)`,
  showDownloadNotice = false,
  downloading = false,
  onDownload,
}: StatementPreviewPanelProps) {
  const previewPaperWidthPx = 1134;
  const [previewScale, setPreviewScale] = useState(0.7);
  const scaledPreviewWidthPx = previewPaperWidthPx * previewScale;
  const [previewContentHeightPx, setPreviewContentHeightPx] = useState(1300);
  const scaledPreviewHeightPx = previewContentHeightPx * previewScale;
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const resizeTimerIdsRef = useRef<number[]>([]);
  const previewContainerRef = useRef<HTMLDivElement | null>(null);
  const containerResizeObserverRef = useRef<ResizeObserver | null>(null);

  const clearResizeTimers = useCallback(() => {
    resizeTimerIdsRef.current.forEach(timerId => window.clearTimeout(timerId));
    resizeTimerIdsRef.current = [];
  }, []);

  const cleanupPreviewResizeObserver = useCallback(() => {
    resizeObserverRef.current?.disconnect();
    resizeObserverRef.current = null;
  }, []);

  const cleanupContainerResizeObserver = useCallback(() => {
    containerResizeObserverRef.current?.disconnect();
    containerResizeObserverRef.current = null;
  }, []);

  const syncPreviewScale = useCallback(() => {
    const container = previewContainerRef.current;

    if (!container) return;

    const availableWidthPx = container.clientWidth - 12;

    if (availableWidthPx <= 0) return;

    const nextScale = Math.min(1, availableWidthPx / previewPaperWidthPx);

    setPreviewScale(previousScale => (
      Math.abs(previousScale - nextScale) < 0.01
        ? previousScale
        : nextScale
    ));
  }, []);

  const syncPreviewHeight = useCallback((iframeEl: HTMLIFrameElement | null) => {
    const documentElement = iframeEl?.contentDocument?.documentElement;
    const body = iframeEl?.contentDocument?.body;

    if (!documentElement || !body) return;

    const nextHeight = Math.max(
      documentElement.scrollHeight,
      documentElement.offsetHeight,
      body.scrollHeight,
      body.offsetHeight,
    );

    if (nextHeight > 0) {
      setPreviewContentHeightPx(nextHeight + 4);
    }
  }, []);

  const onPreviewLoad = useCallback((event: SyntheticEvent<HTMLIFrameElement>) => {
    const iframeEl = event.currentTarget;
    syncPreviewHeight(iframeEl);
    clearResizeTimers();
    cleanupPreviewResizeObserver();

    [120, 350, 700].forEach(delay => {
      const timerId = window.setTimeout(() => syncPreviewHeight(iframeEl), delay);
      resizeTimerIdsRef.current.push(timerId);
    });

    if (typeof ResizeObserver === `undefined`) return;

    const body = iframeEl.contentDocument?.body;
    const documentElement = iframeEl.contentDocument?.documentElement;
    const observer = new ResizeObserver(() => syncPreviewHeight(iframeEl));

    if (body) observer.observe(body);
    if (documentElement) observer.observe(documentElement);

    resizeObserverRef.current = observer;
  }, [clearResizeTimers, cleanupPreviewResizeObserver, syncPreviewHeight]);

  useEffect(() => {
    setPreviewContentHeightPx(1300);
  }, [html]);

  useEffect(() => {
    syncPreviewScale();
    window.setTimeout(syncPreviewScale, 0);

    if (typeof ResizeObserver === `undefined`) return;

    const container = previewContainerRef.current;

    if (!container) return;

    cleanupContainerResizeObserver();

    const observer = new ResizeObserver(() => syncPreviewScale());

    observer.observe(container);
    containerResizeObserverRef.current = observer;
  }, [cleanupContainerResizeObserver, syncPreviewScale]);

  useEffect(() => () => {
    clearResizeTimers();
    cleanupPreviewResizeObserver();
    cleanupContainerResizeObserver();
  }, [clearResizeTimers, cleanupContainerResizeObserver, cleanupPreviewResizeObserver]);

  if (!html) {
    return null;
  }

  return (
    <>
      <div
        ref={ previewContainerRef }
        className={ `flex justify-center w-full overflow-y-auto overflow-x-hidden rounded-lg border border-slate-300 bg-slate-200/70 p-6` }
        style={ { maxHeight } }
      >
        <div
          style={ {
            width: `${scaledPreviewWidthPx}px`,
            minHeight: `${scaledPreviewHeightPx}px`,
          } }
        >
          <CustomIframe
            style={ {
              height: `${previewContentHeightPx}px`,
              width: `${previewPaperWidthPx}px`,
              transform: `scale(${previewScale})`,
              transformOrigin: `top left`,
              marginRight: `-${previewPaperWidthPx - scaledPreviewWidthPx}px`,
              marginBottom: `-${previewContentHeightPx - scaledPreviewHeightPx}px`,
              border: `1px solid #cbd5e1`,
              backgroundColor: `#ffffff`,
              boxShadow: `0 14px 30px rgba(15, 23, 42, 0.18)`,
              display: `block`,
            } }
            onLoad={ onPreviewLoad }
            scrolling={ `no` }
            srcDoc={ html }
          />
        </div>
      </div>

      <When condition={ showDownloadNotice && !!onDownload }>
        <Notification
          type={ `info` }
          className={ `mt-4` }
        >
          <Paragraph as={ `span` }>
            { `Exact spacings and sizing may differ slightly on the PDF. Click ` }
          </Paragraph>
          <Paragraph
            variant={ `link` }
            as={ `span` }
          >
            <a
              onClick={ event => {
                event.preventDefault();
                onDownload?.();
              } }
            >
              { `here` }
            </a>
          </Paragraph>
          <Paragraph as={ `span` }>
            { ` to download the PDF.` }
          </Paragraph>

          <When condition={ downloading }>
            <Paragraph
              as={ `span` }
              className={ `ml-2` }
            >
              { `Downloading...` }
            </Paragraph>
          </When>
        </Notification>
      </When>
    </>
  );
}
